import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Modal, message, Select, Input, InputNumber } from 'antd';
import { Form, InputGroup, Badge } from 'react-bootstrap';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, SyncOutlined } from '@ant-design/icons';
import { oneKlicks } from "../../../SDK/oneclick";
import { getuserdata } from "../../../services/apiService";
import { OneKlicksFileSDK } from "../../../SDK/OneKlicksFileSDK";
import { toast } from "react-toastify";
import ImageShow from "../../../Uitiles/ImageShow";
import Paginationnew from "../../../Uitiles/Paginationnew";

const { TextArea } = Input;
const { Option } = Select;


// Enhanced initial form with all required fields matching database structure
const initialForm = {
    product_name: "",
    brand: "",
    category: "",
    description: "",
    image: "",
    price: 0,
    status: "active",
    prescription_required: 0
};

const Products = () => {
    // State management
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [pharmacyId, setPharmacyId] = useState(null);
    const [formData, setFormData] = useState(initialForm);

    // Fetch pharmacy products with pagination and search
    const fetchProducts = useCallback(async () => {
        if (!pharmacyId) return;
        try {
            setLoading(true);
            const tableSchema = "";
            const tableName = "pharmacy_products";

            // Filter by pharmacy_id as requested
            const conditions = { pharmacy_id: pharmacyId };

            const orderBy = "id DESC";
            const search = searchText?.trim() ? {
                product_name: searchText.trim(),
                brand: searchText.trim(),
                category: searchText.trim()
            } : {};
            const pagination = {
                page: page,
                limit: limit
            };

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination);

            if (response.success) {
                const productData = response.data || [];
                setProducts(productData);
                setTotalPages(response.totalPages);
            } else {
                console.error('API returned error:', response);

                // CHECK FOR SCHEMA ERROR IN RESPONSE
                const errorStr = JSON.stringify(response).toLowerCase();
                if (errorStr.includes("unknown column") || errorStr.includes("pharmacy_id")) {
                    await handleSchemaFix();
                    return;
                }

                message.error('Failed to fetch products');
            }
        } catch (error) {
            console.error('Error fetching products:', error);

            // CHECK FOR SCHEMA ERROR IN CATCH
            const errorMsg = (error.message || JSON.stringify(error)).toLowerCase();
            if (errorMsg.includes("unknown column") || errorMsg.includes("pharmacy_id")) {
                await handleSchemaFix();
                return;
            }

            message.error('Failed to fetch products');
        } finally {
            setLoading(false);
        }
    }, [page, limit, searchText, pharmacyId]);

    // Independent function to handle schema repair
    const handleSchemaFix = async () => {
        if (!pharmacyId) return;

        // Prevent infinite loops using Session Storage
        if (sessionStorage.getItem('schema_fix_attempted')) {
            console.warn("Schema fix already attempted this session. Stopping loop.");
            // Force showing manual SQL if loop detected
            message.error("Auto-fix failed. Please run SQL manually: ALTER TABLE pharmacy_products ADD pharmacy_id VARCHAR(255);", 10);
            return;
        }

        console.warn("Detected missing column pharmacy_id. Attempting auto-fix...");
        sessionStorage.setItem('schema_fix_attempted', 'true');
        message.loading("Fixing database schema...", 2);

        try {
            const tableSchema = JSON.stringify({ pharmacy_id: "VARCHAR(255) NULL" });
            const tableName = "pharmacy_products";

            // Force Creation via Dummy Record
            await oneKlicks.create(tableSchema, tableName, {
                pharmacy_id: pharmacyId.toString(),
                product_name: "Schema Fix",
                description: "Auto-fix column",
                price: "0",
                status: "deleting"
            }, {});

            // Cleanup
            await oneKlicks.delete("", tableName, { product_name: "Schema Fix" });

            // Migrate Existing Products
            // We fetch ALL products (no filter) to find those needing update
            const allProductsRes = await oneKlicks.read("", tableName, {}, "id DESC", {}, { page: 1, limit: 1000 });
            if (allProductsRes.success && allProductsRes.data) {
                let count = 0;
                for (const p of allProductsRes.data) {
                    // Update if pharmacy_id is missing or needs assignment
                    if (!p.pharmacy_id) {
                        await oneKlicks.update(
                            tableSchema,
                            tableName,
                            { pharmacy_id: pharmacyId.toString() },
                            { id: p.id }
                        );
                        count++;
                    }
                }
                if (count > 0) message.success(`Migrated ${count} products.`);
            }

            // Reload to verify
            message.success("Database fixed! Reloading...");
            window.location.reload();

        } catch (fixErr) {
            console.error("Auto-fix failed:", fixErr);
            message.error("Auto-fix failed. Please run SQL manually.");
        }
    };


    // Fetch categories from pharmacy_categories table
    const fetchCategories = useCallback(async () => {
        try {
            const tableSchema = "";
            const tableName = "pharmacy_categories";
            const conditions = {};
            const orderBy = "name ASC";
            const search = {};
            const paginationData = { page: 1, limit: 100 };

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, paginationData);

            if (response.success) {
                const categoryData = response.data || [];
                setCategories(categoryData);
            } else {
                // Set default categories if table doesn't exist
                // setCategories([
                //     { id: 1, name: 'Antibiotics' },
                //     { id: 2, name: 'Pain Relief' },
                //     { id: 3, name: 'Vitamins & Supplements' },
                //     { id: 4, name: 'First Aid' },
                //     { id: 5, name: 'Personal Care' },
                //     { id: 6, name: 'Baby Care' },
                //     { id: 7, name: 'Women Health' },
                //     { id: 8, name: 'Men Health' },
                //     { id: 9, name: 'Diabetes Care' },
                //     { id: 10, name: 'Heart Health' },
                //     { id: 11, name: 'Other' }
                // ]);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            // Set default categories on error
            setCategories([
                { id: 1, name: 'Antibiotics' },
                { id: 2, name: 'Pain Relief' },
                { id: 3, name: 'Vitamins & Supplements' },
                { id: 4, name: 'First Aid' },
                { id: 5, name: 'Personal Care' },
                { id: 6, name: 'Baby Care' },
                { id: 7, name: 'Women Health' },
                { id: 8, name: 'Men Health' },
                { id: 9, name: 'Diabetes Care' },
                { id: 10, name: 'Heart Health' },
                { id: 11, name: 'Other' }
            ]);
        }
    }, []);

    // Fetch brands from pharmacy_brands table
    const fetchBrands = useCallback(async () => {
        try {
            const tableSchema = "";
            const tableName = "pharmacy_brands";
            const conditions = {};
            const orderBy = "name ASC";
            const search = {};
            const paginationData = { page: 1, limit: 100 };

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, paginationData);

            if (response.success) {
                const brandData = response.data || [];
                setBrands(brandData);
            } else {
                // Set default brands if table doesn't exist
                setBrands([
                    { id: 1, name: 'Pfizer' },
                    { id: 2, name: 'Johnson & Johnson' },
                    { id: 3, name: 'Novartis' },
                    { id: 4, name: 'Roche' },
                    { id: 5, name: 'Merck' },
                    { id: 6, name: 'GlaxoSmithKline' },
                    { id: 7, name: 'AstraZeneca' },
                    { id: 8, name: 'Bayer' },
                    { id: 9, name: 'Sanofi' },
                    { id: 10, name: 'Other' }
                ]);
            }
        } catch (error) {
            console.error('Error fetching brands:', error);
            // Set default brands on error
            setBrands([
                { id: 1, name: 'Pfizer' },
                { id: 2, name: 'Johnson & Johnson' },
                { id: 3, name: 'Novartis' },
                { id: 4, name: 'Roche' },
                { id: 5, name: 'Merck' },
                { id: 6, name: 'GlaxoSmithKline' },
                { id: 7, name: 'AstraZeneca' },
                { id: 8, name: 'Bayer' },
                { id: 9, name: 'Sanofi' },
                { id: 10, name: 'Other' }
            ]);
        }
    }, []);

    // Initialize pharmacy ID and load data
    useEffect(() => {
        const initializePharmacy = async () => {
            try {
                const id = await getuserdata("id");
                if (id) {
                    setPharmacyId(id);
                } else {
                    message.error("Login required");
                }
            } catch (error) {
                console.error('Error getting pharmacy ID:', error);
                message.error("Failed to get pharmacy information");
            }
        };
        initializePharmacy();
    }, []);

    useEffect(() => {
        if (pharmacyId) {
            fetchProducts();
            fetchCategories();
            fetchBrands();
        }
    }, [pharmacyId, fetchProducts, fetchCategories, fetchBrands]);

    useEffect(() => {
        fetchProducts();
    }, [searchText, page]);

    // Search functionality
    const handleSearch = (value) => {
        setSearchText(value);
        setPage(1);
    };

    // Debounced search to improve performance
    const debouncedSearch = useCallback(
        (() => {
            let timeoutId;
            return (value) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    handleSearch(value);
                }, 300);
            };
        })(),
        []
    );

    // Pagination handler
    const handlePageChange = (page) => {
        setPage(page);
    };

    // Modal handlers
    const showModal = () => {
        setFormData(initialForm);
        setEditData(null);
        setShowAddModal(true);
        setShowEditModal(false);
    };

    const closeModal = () => {
        setShowAddModal(false);
        setShowEditModal(false);
        setEditData(null);
        setFormData(initialForm);
    };

    const handleShowEditModal = (record) => {
        setEditData(record);
        setFormData({
            product_name: record.product_name || "",
            brand: record.brand || "",
            category: record.category || "",
            description: record.description || "",
            image: record.image || "",
            price: record.price || 0,
            status: record.status || "active",
            prescription_required: record.prescription_required || 0
        });
        setShowEditModal(true);
        setShowAddModal(false);
    };

    // Delete product
    const handleDelete = async (record, event) => {
        if (event) {
            event.preventDefault();
        }
        const confirmDelete = window.confirm(`Are you sure you want to delete "${record.product_name}"?`);

        if (confirmDelete) {
            try {
                const tableSchema = "";
                const tableName = "pharmacy_products";
                const conditions = { id: record.id };

                const response = await oneKlicks.delete(tableSchema, tableName, conditions);

                if (response.success) {
                    message.success('Product deleted successfully');
                    if (record.image) {
                        OneKlicksFileSDK.deleteFile(record.image).then(response => {
                            console.log(response);
                            fetchProducts();
                        });
                    } else {
                        fetchProducts();
                    }
                } else {
                    message.error(response.message || 'Failed to delete product');
                }
            } catch (error) {
                console.error('Error deleting product:', error);
                message.error('Failed to delete product');
            }
        }
    };

    // Toggle product status
    const handleToggleStatus = async (record) => {
        try {
            const tableSchema = "";
            const tableName = "pharmacy_products";
            const nextStatus = record.status === "active" ? "inactive" : "active";
            const statusText = nextStatus === "active" ? 'Active' : 'Inactive';
            const conditions = { id: record.id };
            const response = await oneKlicks.update(tableSchema, tableName, { status: nextStatus }, conditions);

            if (response.success) {
                message.success(`Status changed to ${statusText}`);
                await fetchProducts();
            } else {
                message.error(response.message || 'Failed to update status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            message.error('Failed to update status');
        }
    };

    // Assign all current products to this pharmacy
    const handleAssignProducts = async () => {
        if (!pharmacyId || products.length === 0) return;

        const confirmAssign = window.confirm(`Assign all ${products.length} products to your pharmacy (ID: ${pharmacyId})?`);
        if (!confirmAssign) return;

        setLoading(true);
        try {
            // STEP 1: Attempt to force column creation via a dummy CREATE operation
            const tableSchema = JSON.stringify({
                pharmacy_id: "VARCHAR(255) NULL"
            });
            const tableName = "pharmacy_products";

            console.log("Attempting to force column creation...");
            const dummyData = {
                pharmacy_id: pharmacyId.toString(),
                product_name: "System Column Fix",
                description: "Temporary record to fix schema",
                price: "0",
                status: "deleting"
            };

            try {
                // Create dummy record to trigger schema update
                await oneKlicks.create(tableSchema, tableName, dummyData, {});
                // Immediately delete it
                await oneKlicks.delete("", tableName, { product_name: "System Column Fix" });
            } catch (e) {
                console.warn("Dummy create failed (might be okay if column exists):", e);
            }

            // STEP 2: Update actual products
            let successCount = 0;
            for (const product of products) {
                const conditions = { id: product.id };
                const updateData = { pharmacy_id: pharmacyId.toString() };

                const response = await oneKlicks.update(tableSchema, tableName, updateData, conditions);
                if (response.success) successCount++;
            }
            message.success(`Successfully assigned ${successCount} products to your pharmacy!`);

            window.location.reload();

        } catch (error) {
            console.error('Error assigning products:', error);
            // Provide explicit SQL instruction in case of failure
            message.error('Fix failed. Please run SQL manually: ALTER TABLE pharmacy_products ADD pharmacy_id VARCHAR(255);', 10);
        } finally {
            setLoading(false);
        }
    };

    // Form submission handler
    const handleSubmit = async () => {
        const values = formData;
        try {
            setSubmitLoading(true);
            const tableSchema = "";
            const tableName = "pharmacy_products";

            // Check for duplicate product names (excluding current record if editing)
            const existingProduct = products.find(product =>
                product.product_name.toLowerCase() === values.product_name.toLowerCase() &&
                (!editData || product.id !== editData.id)
            );

            if (existingProduct) {
                message.error('A product with this name already exists');
                setSubmitLoading(false);
                return;
            }

            // Validate required fields
            if (!values.product_name || !values.category || !values.brand) {
                message.error('Product name, category, and brand are required');
                setSubmitLoading(false);
                return;
            }

            // Validate price
            if (values.price < 0) {
                message.error('Price cannot be negative');
                setSubmitLoading(false);
                return;
            }

            // Handle image upload if there's a file
            let imageValue = '';
            if (formData.imageFile && formData.imageFile instanceof File) {
                try {
                    const uploadFormData = new FormData();
                    uploadFormData.append('userapikey', process.env.REACT_APP_API_KEY_ONEKLICKS);
                    uploadFormData.append('root', 'pharmacy_products');
                    uploadFormData.append('status', 'public');
                    uploadFormData.append('file', formData.imageFile);

                    const API_URL = process.env.REACT_APP_API_BASE_URL || 'https://apis.oneklicks.com';
                    const response = await fetch(`${API_URL}/api/files/uploads`, {
                        method: 'POST',
                        headers: {
                            'x-api-key': process.env.REACT_APP_API_KEY_ONEKLICKS,
                        },
                        body: uploadFormData
                    });

                    const uploadResponse = await response.json();
                    console.log('Upload Response:', uploadResponse);

                    if (uploadResponse.success) {
                        imageValue = uploadResponse.fileId;
                    } else {
                        message.error(uploadResponse.error || uploadResponse.message || 'Failed to upload image');
                        setSubmitLoading(false);
                        return;
                    }
                } catch (uploadError) {
                    console.error('Upload Error:', uploadError);
                    message.error('Failed to upload image: ' + uploadError.message);
                    setSubmitLoading(false);
                    return;
                }
            } else if (editData && editData.image) {
                // Keep existing image if editing and no new image uploaded
                imageValue = editData.image;
            }

            // Prepare data matching database structure
            const data = {
                pharmacy_id: pharmacyId,
                product_name: values.product_name,
                brand: values.brand,
                category: values.category,
                description: values.description || '',
                price: parseFloat(values.price) || 0,
                status: values.status,
                prescription_required: values.prescription_required === 1 || values.prescription_required === "1" ? 1 : 0,
                image: imageValue
            };

            if (showEditModal && editData) {
                // Update existing product
                const conditions = { id: editData.id };
                const response = await oneKlicks.update(tableSchema, tableName, data, conditions);

                if (response.success) {
                    message.success('Product updated successfully');
                    closeModal();
                    await fetchProducts();
                } else {
                    message.error(response.message || 'Failed to update product');
                }
            } else {
                // Create new product
                const response = await oneKlicks.create(tableSchema, tableName, data);

                if (response.success) {
                    message.success('Product created successfully');
                    closeModal();
                    await fetchProducts();
                } else {
                    message.error(response.message || 'Failed to create product');
                }
            }
        } catch (error) {
            console.error('Error saving product:', error);
            message.error('Failed to save product');
        } finally {
            setSubmitLoading(false);
        }
    };

    // View product details modal
    const showProductDetails = (record) => {
        setSelectedProduct(record);
        setShowDetailsModal(true);
    };

    return (
        <>
            <div className="container-fluid py-3">
                <div className="card mb-5">
                    <div className="card-body">
                        <div className="align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <h5 className="mb-0 font-weight-bold mr-3" style={{ color: 'white' }}>Pharmacy Products Management</h5>
                                <Badge bg="info">Products</Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                                <InputGroup className="flex-grow-1 me-2 h-40">
                                    <Form.Control
                                        placeholder="Search products by name, brand, or category..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </InputGroup>

                                <Button className="btn-success h-40 mr-2" onClick={handleAssignProducts} disabled={loading || products.length === 0}>
                                    <SyncOutlined /> Claim All Products
                                </Button>

                                <Button className="btn-primary h-40" onClick={showModal}>
                                    <PlusOutlined /> Add New Product
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <table hover size="sm" className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Image</th>
                                        <th>Product Details</th>
                                        <th>Category</th>
                                        <th>Price</th>
                                        <th>Prescription</th>
                                        <th>Status</th>
                                        <th style={{ width: '320px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="8" className="text-center py-4">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : products.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="text-center py-5">
                                                <h5 className="text-muted mb-2">No products found</h5>
                                                <p className="text-muted mb-3">
                                                    {searchText ? 'Try adjusting your search criteria' : 'Get started by adding your first pharmacy product'}
                                                </p>
                                                {!searchText && (
                                                    <Button className="btn-primary" onClick={showModal}>
                                                        <PlusOutlined /> Add First Product
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ) : (
                                        products.map((item) => (
                                            <tr key={item.id}>
                                                <td data-label="ID">{item.id}</td>
                                                <td>
                                                    {item.image ? (
                                                        <ImageShow image={item.image} width={50} height={50} style={{ objectFit: 'cover', borderRadius: '4px', width: '50px', height: '50px', boxShadow: '0px 0px 4px 0px black' }} />
                                                    ) : (
                                                        <div style={{
                                                            width: '50px',
                                                            height: '50px',
                                                            backgroundColor: '#f0f8ff',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            borderRadius: '4px',
                                                            border: '1px solid #e6f7ff'
                                                        }}>
                                                            <i className="fas fa-pills" style={{ color: '#1890ff', fontSize: '20px' }}></i>
                                                        </div>
                                                    )}
                                                </td>
                                                <td data-label="Product Details">
                                                    <div>
                                                        <div style={{ fontWeight: '600', color: '#1890ff', marginBottom: '2px' }}>
                                                            {item.product_name}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#666' }}>{item.brand}</div>
                                                    </div>
                                                </td>
                                                <td data-label="Category">
                                                    <Badge bg="primary">{item.category}</Badge>
                                                </td>
                                                <td data-label="Price" style={{ fontWeight: '600', color: '#52c41a' }}>
                                                    ${parseFloat(item.price || 0).toFixed(2)}
                                                </td>
                                                <td data-label="Prescription">
                                                    <Badge bg={item.prescription_required === 1 ? "danger" : "success"}>
                                                        {item.prescription_required === 1 ? 'Required' : 'Not Required'}
                                                    </Badge>
                                                </td>
                                                <td data-label="Status" style={{ textAlign: 'center' }}>
                                                    <Button
                                                        onClick={() => handleToggleStatus(item)}
                                                        style={{
                                                            color: item.status === 'active' ? '#52c41a' : '#ff4d4f',
                                                        }}
                                                        onMouseOver={(e) => {
                                                            e.target.style.transform = 'scale(1.02)';
                                                            e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.12)';
                                                        }}
                                                        onMouseOut={(e) => {
                                                            e.target.style.transform = 'scale(1)';
                                                            e.target.style.boxShadow = '0 1px 2px rgba(0,0,0,0.06)';
                                                        }}
                                                    >
                                                        {item.status === 'active' ? 'Active' : 'Inactive'}
                                                    </Button>
                                                </td>
                                                <td data-label="Action">
                                                    <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                                                        <Button href="#" className="dc-update-servicesss fit-content btn-info" onClick={(e) => {
                                                            e.preventDefault();
                                                            showProductDetails(item);
                                                        }}>
                                                            <EyeOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                        </Button>
                                                        <Button href="#" className="dc-update-servicesss fit-content" onClick={(e) => {
                                                            e.preventDefault();
                                                            handleShowEditModal(item);
                                                        }}>
                                                            <EditOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                        </Button>
                                                        <Button href="#" className="dc-update-servicesss btn-danger" onClick={(e) => {
                                                            e.preventDefault();
                                                            handleDelete(item, e);
                                                        }}>
                                                            <DeleteOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Paginationnew currentPage={page} totalPages={totalPages} handlePageChange={handlePageChange} />
                    </div>
                </div>

                {/* Add/Edit Product Modal */}
                <Modal
                    title={showEditModal ? "Edit Product" : "Add New Product"}
                    open={showAddModal || showEditModal}
                    onCancel={closeModal}
                    onOk={handleSubmit}
                    confirmLoading={submitLoading}
                    width={800}
                >
                    <div style={{ marginTop: '20px' }}>
                        <div className="row">
                            <div className="col-md-6">
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        marginBottom: '8px',
                                        display: 'block',
                                        fontWeight: '600',
                                        color: '#374151',
                                        fontSize: '14px'
                                    }}>
                                        Product Name <span style={{ color: '#ff4d4f' }}>*</span>
                                    </label>
                                    <Input
                                        placeholder="Enter product name"
                                        value={formData.product_name}
                                        onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                                        required
                                        size="large"
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        marginBottom: '8px',
                                        display: 'block',
                                        fontWeight: '600',
                                        color: '#374151',
                                        fontSize: '14px'
                                    }}>
                                        Brand <span style={{ color: '#ff4d4f' }}>*</span>
                                    </label>
                                    <Select
                                        style={{ width: '100%' }}
                                        placeholder="Select brand"
                                        value={formData.brand}
                                        onChange={(value) => setFormData({ ...formData, brand: value })}
                                        size="large"
                                    >
                                        {brands.map(brand => (
                                            <Option key={brand.id} value={brand.name}>{brand.name}</Option>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        marginBottom: '8px',
                                        display: 'block',
                                        fontWeight: '600',
                                        color: '#374151',
                                        fontSize: '14px'
                                    }}>
                                        Category <span style={{ color: '#ff4d4f' }}>*</span>
                                    </label>
                                    <Select
                                        style={{ width: '100%' }}
                                        placeholder="Select category"
                                        value={formData.category}
                                        onChange={(value) => setFormData({ ...formData, category: value })}
                                        size="large"
                                    >
                                        {categories.map(category => (
                                            <Option key={category.id} value={category.name}>{category.name}</Option>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        marginBottom: '8px',
                                        display: 'block',
                                        fontWeight: '600',
                                        color: '#374151',
                                        fontSize: '14px'
                                    }}>
                                        Price ($) <span style={{ color: '#ff4d4f' }}>*</span>
                                    </label>
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        placeholder="0.00"
                                        min={0}
                                        step={0.01}
                                        value={formData.price}
                                        onChange={(value) => setFormData({ ...formData, price: value || 0 })}
                                        size="large"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        marginBottom: '8px',
                                        display: 'block',
                                        fontWeight: '600',
                                        color: '#374151',
                                        fontSize: '14px'
                                    }}>
                                        Status <span style={{ color: '#ff4d4f' }}>*</span>
                                    </label>
                                    <Select
                                        style={{ width: '100%' }}
                                        value={formData.status}
                                        onChange={(value) => setFormData({ ...formData, status: value })}
                                        size="large"
                                    >
                                        <Option value="active">Active</Option>
                                        <Option value="inactive">Inactive</Option>
                                    </Select>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        marginBottom: '8px',
                                        display: 'block',
                                        fontWeight: '600',
                                        color: '#374151',
                                        fontSize: '14px'
                                    }}>
                                        Prescription Required
                                    </label>
                                    <Select
                                        style={{ width: '100%' }}
                                        value={formData.prescription_required}
                                        onChange={(value) => setFormData({ ...formData, prescription_required: value })}
                                        size="large"
                                    >
                                        <Option value={0}>Not Required</Option>
                                        <Option value={1}>Required</Option>
                                    </Select>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                marginBottom: '8px',
                                display: 'block',
                                fontWeight: '600',
                                color: '#374151',
                                fontSize: '14px'
                            }}>
                                Description
                            </label>
                            <TextArea
                                rows={3}
                                placeholder="Enter product description..."
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                style={{ resize: 'vertical' }}
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                marginBottom: '8px',
                                display: 'block',
                                fontWeight: '600',
                                color: '#374151',
                                fontSize: '14px'
                            }}>
                                Product Image
                            </label>
                            <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                        setFormData({ ...formData, imageFile: e.target.files[0] });
                                    }
                                }}
                                style={{ padding: '4px' }}
                            />
                            {editData?.image && (
                                <div style={{
                                    marginTop: '12px',
                                    padding: '12px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '6px',
                                    border: '1px solid #e9ecef'
                                }}>
                                    <small style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        color: '#6c757d',
                                        fontWeight: '500'
                                    }}>
                                        Current image:
                                    </small>
                                    <div style={{ marginTop: '8px' }}>
                                        <ImageShow image={editData.image} width={100} height={100} style={{ borderRadius: '6px', border: '1px solid #dee2e6' }} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>

                {/* Product Details Modal */}
                <Modal
                    title="Product Details"
                    open={showDetailsModal}
                    onCancel={() => setShowDetailsModal(false)}
                    footer={[
                        <Button key="close" onClick={() => setShowDetailsModal(false)}>
                            Close
                        </Button>
                    ]}
                    width={600}
                >
                    {selectedProduct && (
                        <div>
                            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                {selectedProduct.image ? (
                                    <ImageShow image={selectedProduct.image} width={200} height={150} style={{ borderRadius: '8px' }} />
                                ) : (
                                    <div style={{
                                        width: '200px',
                                        height: '150px',
                                        backgroundColor: '#f0f8ff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '8px',
                                        margin: '0 auto',
                                        border: '1px solid #e6f7ff'
                                    }}>
                                        <i className="fas fa-pills" style={{ color: '#1890ff', fontSize: '48px' }}></i>
                                    </div>
                                )}
                            </div>
                            <div style={{ marginBottom: '20px' }}>
                                <h4>{selectedProduct.product_name}</h4>
                                <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', marginTop: '15px' }}>
                                    <p><strong>Brand:</strong> {selectedProduct.brand}</p>
                                    <p><strong>Category:</strong> {selectedProduct.category}</p>
                                    <p><strong>Price:</strong> ${parseFloat(selectedProduct.price || 0).toFixed(2)}</p>
                                    <p><strong>Prescription Required:</strong> {selectedProduct.prescription_required === 1 ? 'Yes' : 'No'}</p>
                                    <p><strong>Status:</strong>
                                        <Badge bg={selectedProduct.status === 'active' ? "success" : "danger"} style={{ marginLeft: '8px' }}>
                                            {selectedProduct.status}
                                        </Badge>
                                    </p>
                                    <p><strong>Description:</strong></p>
                                    <p style={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        backgroundColor: '#fff',
                                        padding: '10px',
                                        borderRadius: '4px',
                                        border: '1px solid #d9d9d9',
                                        marginTop: '5px'
                                    }}>
                                        {selectedProduct.description || 'No description available'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal>
            </div>
        </>
    );
};

export default Products;
